/**
 * @fileoverview Defines `GoogleApi` and `GoogleWithCaching` classes for interacting with Google's Gemini models. `GoogleApi` implements basic Gemini interaction, while `GoogleWithCaching` adds a caching layer using `GoogleAICacheManager`. Includes utility functions like `promptGeminiWithLooping` and `promptGeminiWithStructuredOutput`.
 */

import { createGoogleGenerativeAI, google } from '@ai-sdk/google'
import {
  CachedContent,
  Content,
  EnhancedGenerateContentResponse,
  GoogleGenerativeAI,
} from '@google/generative-ai'
import { GoogleAICacheManager } from '@google/generative-ai/server'
import { generateObject, generateText, LanguageModelUsage, LanguageModelV1, streamText } from 'ai'
import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'fs'
import path from 'path'
import { debug } from '../util/logging'
import { tokenCount } from '../util/tokens'
import { trackCost } from '../util/usage_tracker'
import { ApiKeys, KeyProvider } from './api_keys'
import {
  InputTool,
  LlmApi,
  Message,
  Model,
  ModelNeedingKeys,
  ResponseToolCall,
  Usage,
} from './llm_types'
import { z } from 'zod'

export const Gemini20FlashLite: ModelNeedingKeys = {
  name: 'gemini-2.0-flash-lite',
  promptTokenCost: 0.075 / 1000000,
  completionTokenCost: 0.3 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-2.0-flash-lite-preview-02-05', settings),
}

export const Gemini20Flash: ModelNeedingKeys = {
  name: 'gemini-2.0-flash',
  promptTokenCost: 0.1 / 1000000,
  completionTokenCost: 0.7 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-2.0-flash', settings),
}

// There is different pricing for long context.
export const Gemini20FlashLongContext: ModelNeedingKeys = {
  name: 'gemini-2-flash-long-context',
  promptTokenCost: 0.15 / 1000000,
  completionTokenCost: 0.6 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-2.0-flash', settings),
}

export const Gemini15Flash: ModelNeedingKeys = {
  name: 'gemini-1.5-flash',
  promptTokenCost: 0.075 / 1000000,
  completionTokenCost: 0.3 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-1.5-flash', settings),
}

export const Gemini15FlashLongContext: ModelNeedingKeys = {
  name: 'gemini-1.5-flash-long-context',
  promptTokenCost: 0.15 / 1000000,
  completionTokenCost: 0.6 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-1.5-flash', settings),
}

export const Gemini15Pro: ModelNeedingKeys = {
  name: 'gemini-1.5-pro',
  promptTokenCost: 1.25 / 1000000,
  completionTokenCost: 5 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-1.5-pro', settings),
}

export const Gemini15ProLongContext: ModelNeedingKeys = {
  name: 'gemini-1.5-pro-long-context',
  promptTokenCost: 2.5 / 1000000,
  completionTokenCost: 10 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-1.5-pro', settings),
}

export const Gemini20FlashThinking: ModelNeedingKeys = {
  name: 'gemini-2-flash-thinking',
  promptTokenCost: 0.075 / 1000000,
  completionTokenCost: 0.3 / 1000000,
  getApi: (settings: KeyProvider) => new GoogleApi('gemini-2.0-flash-thinking-exp', settings),
}

export class GoogleApi implements LlmApi {
  requiredKeys: ApiKeys[] = ['GOOGLE_GENERATIVE_AI_API_KEY']
  private client: LanguageModelV1

  constructor(model: string, settings: KeyProvider) {
    const google = createGoogleGenerativeAI({
      apiKey: settings.get('GOOGLE_GENERATIVE_AI_API_KEY'),
    })
    this.client = google(model)
  }

  async chatCompletion(
    _system: string | undefined,
    _texts: string[],
    _tools: InputTool[],
    _conversation: Message[],
  ): Promise<{ message: Message; toolCalls: Array<ResponseToolCall>; usage: Usage }> {
    throw new Error('Method not implemented.')
  }

  async streamChatCompletions(
    system: string | undefined,
    texts: string[],
    conversation: Message[],
    handler: (message: any) => void,
    _prediction?: string,
  ): Promise<{ message: Message; usage: Usage; inputMessages: Message[] }> {
    const messages: Message[] = []
    if (system) {
      messages.push({ role: 'system', content: system })
    }
    messages.push(...texts.map((text) => ({ role: 'user', content: text }) as Message))
    messages.push(...conversation)

    const resultUsage: Usage = {
      promptTokens: 0,
      completionTokens: 0,
    }

    const { textStream } = streamText({
      model: this.client,
      messages,
      onFinish: ({ usage, finishReason }: { usage: LanguageModelUsage; finishReason: string }) => {
        resultUsage.promptTokens = usage.promptTokens
        resultUsage.completionTokens = usage.completionTokens
        if (finishReason !== 'stop' && finishReason !== 'length' && finishReason !== 'tool-calls') {
          throw new Error(finishReason)
        }
      },
    })

    let fullContent = ''
    for await (const chunk of textStream) {
      fullContent += chunk
      handler(chunk)
    }

    return {
      message: {
        role: 'assistant',
        content: fullContent,
      },
      usage: resultUsage,
      inputMessages: messages,
    }
  }
}

export class GoogleWithCaching implements LlmApi {
  requiredKeys: ApiKeys[] = ['GOOGLE_GENERATIVE_AI_API_KEY']

  private MODEL = 'gemini-1.5-flash-002'
  private costsOver128kTokens = {
    promptTokenCost: 0.15 / 1000000,
    completionTokenCost: 0.6 / 1000000,
    cacheTokenCost: 0.0375 / 1000000,
  }
  private costsUnder128kTokens = {
    promptTokenCost: 0.075 / 1000000,
    completionTokenCost: 0.3 / 1000000,
    cacheTokenCost: 0.01875 / 1000000,
  }
  cacheStorageCostPerHour = 1 / 1000000

  // $1 per hour per million tokens stored
  public model: Model = {
    name: 'gemini-1.5-flash',
    promptTokenCost: 0.15 / 1000000,
    completionTokenCost: 0.6 / 1000000,
    getApi: () => this,
  }

  private apiVersion = 'v1beta'

  private googleGenAi: GoogleGenerativeAI
  private cacheManager: GoogleAICacheManager
  private cache: CachedContent | undefined

  constructor(
    settings: KeyProvider,
    private dirForCache: string,
    private cacheKeyFileName: string = 'documentationCacheKey.txt',
    private systemPrompt?: string,
    private usageTag?: string,
  ) {
    const apiKey = settings.get('GOOGLE_GENERATIVE_AI_API_KEY')
    if (!apiKey) {
      throw new Error('GOOGLE_GENERATIVE_AI_API_KEY is not set')
    }
    this.googleGenAi = new GoogleGenerativeAI(apiKey)
    this.cacheManager = new GoogleAICacheManager(apiKey, { apiVersion: this.apiVersion })
  }

  async chatCompletion(
    _system: string | undefined,
    _texts: string[],
    _tools: InputTool[],
    _conversation: Message[],
  ): Promise<{ message: Message; toolCalls: Array<ResponseToolCall>; usage: Usage }> {
    throw new Error('Method not implemented.')
  }

  private readCacheKey() {
    const keyPath = path.join(this.dirForCache, this.cacheKeyFileName)
    if (existsSync(keyPath)) {
      return readFileSync(keyPath, 'utf8')
    }
    return undefined
  }

  private writeCacheKey(key: string) {
    const keyPath = path.join(this.dirForCache, this.cacheKeyFileName)
    if (!existsSync(this.dirForCache)) {
      mkdirSync(this.dirForCache, { recursive: true })
    }
    writeFileSync(keyPath, key, 'utf8')
  }

  async cacheContent(
    system: string,
    content: string,
    trackingName?: string,
    ttlSeconds: number = 300,
  ): Promise<CachedContent & { cacheHit: boolean }> {
    const cacheKey = this.readCacheKey()
    if (cacheKey) {
      try {
        const cache = await this.cacheManager.get(cacheKey)
        if (cache) {
          this.cache = cache
          return { ...cache, cacheHit: true }
        }
      } catch (e) {
        // ignore errors getting cache. this means it's not there. just re-cache
      }
    }

    this.cache = await this.cacheManager.create({
      model: this.MODEL,
      systemInstruction: { role: 'system', parts: [{ text: system }] },
      contents: [{ role: 'user', parts: [{ text: content }] }],
      ttlSeconds,
    })

    // Track async since getTokenCount can be slow
    new Promise(() => {
      if (this.cache?.name) {
        this.writeCacheKey(this.cache.name)
      }
      const tokens = tokenCount(system + content)
      trackCost(
        trackingName + '(create cache item)',
        this.MODEL,
        tokens * this.cacheStorageCostPerHour * (ttlSeconds / (60 * 60)),
        {
          tokens,
          tag: this.usageTag,
        },
      )
    })

    return { ...this.cache, cacheHit: false }
  }

  async streamChatCompletions(
    system: string | undefined,
    texts: string[],
    conversation: Message[],
    handler: (message: any) => void,
    _prediction?: string,
    trackingName?: string,
  ): Promise<{
    message: Message
    usage: Usage
    inputMessages: Message[]
  }> {
    const contents: Content[] = []
    if (!this.cache && system) {
      contents.push({ role: 'system', parts: [{ text: system }] })
    }
    contents.push(...texts.map((text) => ({ role: 'user', parts: [{ text }] })))
    contents.push(
      ...conversation.map((message) => ({
        role: message.role,
        parts: [{ text: message.content }],
      })),
    )

    const startTime = Date.now()
    const result = await this.getGenerativeModel().generateContentStream({
      contents,
      generationConfig: { maxOutputTokens: 8192 },
    })

    let fullContent = ''
    for await (const chunk of result.stream) {
      const text = chunk.text()
      if (fullContent.length === 0) {
        debug(`Time to first token: ${Date.now() - startTime}ms`)
      }
      fullContent += text
      handler(text)
    }

    const usage = await this.trackCost(result.response, trackingName)
    return {
      message: {
        role: 'assistant',
        content: fullContent,
      },
      usage: {
        promptTokens: usage?.promptTokenCount || 0,
        completionTokens: usage?.candidatesTokenCount || 0,
      },
      inputMessages: contents.map((content) => ({
        role: content.role,
        content: content.parts[0]?.text || '',
      })) as Message[],
    }
  }

  private getGenerativeModel() {
    if (this.cache) {
      return this.googleGenAi.getGenerativeModelFromCachedContent(
        this.cache,
        { model: this.MODEL },
        {
          apiVersion: this.apiVersion,
        },
      )
    }
    return this.googleGenAi.getGenerativeModel(
      {
        model: this.MODEL,
        systemInstruction: this.systemPrompt,
      },
      { apiVersion: this.apiVersion },
    )
  }

  private async trackCost(result: Promise<EnhancedGenerateContentResponse>, trackingName?: string) {
    const usage = (await result).usageMetadata
    const costs =
      (usage?.promptTokenCount || 0) > 128000 ? this.costsOver128kTokens : this.costsUnder128kTokens
    const promptCost =
      costs.promptTokenCost *
      ((usage?.promptTokenCount || 0) - (usage?.cachedContentTokenCount || 0))
    const completionCost = costs.completionTokenCost * (usage?.candidatesTokenCount || 0)
    const cacheCost = costs.cacheTokenCost * (usage?.cachedContentTokenCount || 0)
    trackCost(
      trackingName || 'streamChatCompletions',
      this.MODEL,
      promptCost + completionCost + cacheCost,
      {
        promptTokens: usage?.promptTokenCount || 0,
        completionTokens: usage?.candidatesTokenCount || 0,
        cachedContentTokens: usage?.cachedContentTokenCount || 0,
      },
    )
    return usage
  }
}

// Generate a single long response for the model.
export async function promptGeminiWithLooping(
  model: 'gemini-2.0-flash-lite-preview-02-05' | 'gemini-2.0-flash',
  system: string,
  prompt: string,
): Promise<{ text: string; usage: Usage }> {
  const { text, usage } = await generateText({
    model: google(model),
    system,
    prompt,
    experimental_continueSteps: true,
    maxSteps: 30,
  })
  return { text, usage }
}

export async function promptGeminiWithStructuredOutput<T>(
  model: 'gemini-2.0-flash-lite-preview-02-05' | 'gemini-2.0-flash',
  system: string,
  prompt: string,
  schema: z.ZodSchema<T>,
): Promise<{ object: T; usage: Usage }> {
  const { object, usage } = await generateObject({
    model: google(model),
    system,
    prompt,
    schema,
  })
  return { object, usage }
}
